import { computed, ComputedRef, Ref, ref, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { ContentEdge, ContentGroupingCriteria } from "@/shared/services/graphql/generated/consumer-graph-types.ts";
import {
    generateContentsGroupedQuery,
    generateGroupItemsQuery,
    GroupedQueryData,
    GroupedQueryResult,
    GroupedQueryVariables,
    GroupItemsEntry,
    GroupItemsQueryVariables,
    GroupListQueryVariables,
} from "@/abilities/documentation/graphql/documentation-grouping.query.ts";
import { DocumentationGroupResult } from "@/shared/documentation/documentation.types.ts";
import {
    ContentGroupResult,
    mapContentGroupsToGroupResult,
} from "@/shared/documentation/mapper/document-grouped.mapper.ts";
import { useLanguageStore } from "@/shared/store/language-store.ts";
import { SearchContext, Total } from "@/shared/search/search.types.ts";
import { isSortResolved, SortEntry } from "@/shared/components/sort-dropdown/sort-dropdown.model.ts";
import { INIT_LOAD_AMOUNT } from "@/shared/environment/consts.ts";
import { FacetSelected } from "@/shared/facets/facets.model.ts";
import { generateQueryFilterForSearch } from "@/shared/facets/facets-filter-generator.ts";
import { consumerClient } from "@/shared/services/apollo-clients/consumer-client.ts";
import { useDocumentationDisplayConfig } from "./documentation-display-config.ts";
import { toContentSort } from "./content-sort.ts";

export interface DocumentGroupedSearchParams {
    isGroupingEnabled: ComputedRef<boolean>;
    groupingCriteria?: Ref<ContentGroupingCriteria | null | undefined>;
    selected?: Ref<FacetSelected[]>;
    sort?: Ref<SortEntry | undefined>;
    searchContext?: Ref<SearchContext>;
}

/**
 * A composable function that fetches the grouped content results - built dynamically from the search
 * context and the display config datafields/localization fragments, like the plain document search query -
 * and maps them to the `GroupedDocumentation` view model.
 *
 * Division of labor: the group list belongs to Apollo (watch query plus `fetchMore`), while the items of a
 * group belong to this composable (`groupHits`, filled by imperative one-shot queries). Everything Apollo
 * does for the list on its own - a changed search starts a new watch query, so a page of the previous one
 * can no longer reach the view - therefore has to be rebuilt by hand for the items: that is `searchGeneration`.
 *
 * @param groupingCriteria - A reference to the grouping criteria.
 * @param isGroupingEnabled - Whether grouping is currently enabled (e.g. no free-text search phrase active).
 * @param selected - The currently selected facets.
 * @param sort - A reference to the sort entry, applied to the representative hits of every group.
 * @param searchContext - A reference to the search context the grouped contents belong to.
 * @returns An object containing the grouped content results, the total group count, loading state, error state,
 *          a function to load the next page of groups and a function to load the next page of items of a group.
 */
export const useDocumentGroupedSearch = ({
    groupingCriteria,
    isGroupingEnabled,
    selected,
    sort,
    searchContext,
}: DocumentGroupedSearchParams) => {
    const languageStore = useLanguageStore();
    //the display config drives the dynamic node fragments and the property item mapping
    const { result: dataDisplayResult, getDataDisplayPropertiesByConfig } = useDocumentationDisplayConfig();

    /**
     * Hits per group, keyed by the group cursor. The group list query does not select any hits - they are
     * loaded lazily once the user expands a group. They are kept outside the Apollo cache on purpose:
     * `ContentGroup` is not normalizable and the groups of the running watch query are cached as a plain
     * array, so writing a group-scoped page back would be merged into the wrong group.
     */
    const groupHits = ref<Record<string, ContentEdge[]>>({});

    /** In-flight item requests per group cursor, used to collapse concurrent load-more calls. */
    const groupItemRequests = new Map<string, Promise<string | undefined>>();

    /** Incremented whenever the loaded items become invalid, so that pages resolving afterwards are discarded. */
    let searchGeneration = 0;

    /**
     * Drops the items loaded so far, because they belong to a result that is gone (changed search) or to a
     * sort order that no longer applies. The requests still in flight are dropped as well: a call made
     * afterwards must not join one of them, as they resolve to `undefined` via the generation check in
     * `fetchGroupItems` and would leave the group empty.
     */
    const invalidateLoadedItems = () => {
        groupHits.value = {};
        groupItemRequests.clear();
        searchGeneration++;
    };

    const groupsQuery = computed(() => generateContentsGroupedQuery(searchContext?.value));

    const groupItemsQuery = computed(() => {
        if (!dataDisplayResult.value) return generateGroupItemsQuery({}, {}, {}, searchContext?.value);

        return generateGroupItemsQuery(
            dataDisplayResult.value.datafieldsFragment.datafields,
            dataDisplayResult.value.datafieldsFragment.localizationDatafields,
            dataDisplayResult.value.nodeFieldsDataFragment,
            searchContext?.value
        );
    });

    /** The filter and context variables both grouped queries need - each adds its own pagination on top. */
    const sharedQueryVariables = computed((): GroupedQueryVariables => {
        const { contentFilter, assetFilter, productId, mechanicId, articleId } = generateQueryFilterForSearch(
            selected?.value ?? [],
            false,
            searchContext?.value
        );

        return {
            filter: contentFilter,
            groupBy: { criteria: groupingCriteria?.value },
            acceptedLanguages: languageStore.dataQueryLanguages,
            assetFilter,
            productId,
            mechanicId,
            articleId,
        };
    });

    const groupListVariables = computed(
        (): GroupListQueryVariables => ({
            ...sharedQueryVariables.value,
            groupAfter: "",
            groupFirst: INIT_LOAD_AMOUNT,
        })
    );

    // groups are only rendered when their items can safely be loaded afterwards: an unresolved
    // sort or a missing display config would fetch the items under a different cache key
    const isGroupsQueryEnabled = computed(
        () => isGroupingEnabled.value && isSortResolved(sort) && !!groupingCriteria?.value && !!dataDisplayResult.value
    );

    const { result, loading, error, fetchMore } = useQuery<
        GroupedQueryData<ContentGroupResult>,
        GroupListQueryVariables
    >(
        groupsQuery,
        groupListVariables,
        computed(() => ({ enabled: isGroupsQueryEnabled.value }))
    );

    /**
     * The grouped contents are reachable under a different path for every search context, because the
     * query is built against the matching root field (`mechanic`, `assets`, `product`, `article` or the
     * plain `contentsGrouped`). This resolves the context specific path once, so that result, total and
     * pagination all read the same node.
     *
     * @param data - A raw result of one of the generated grouped queries.
     * @returns The grouped contents of the current search context.
     */
    const selectGroupedResult = <TGroup>(
        data?: GroupedQueryData<TGroup> | null
    ): GroupedQueryResult<TGroup> | undefined => {
        if (searchContext?.value.mechanic) return data?.mechanic?.relatedContentsGrouped ?? undefined;
        if (searchContext?.value.asset && searchContext?.value.product)
            return data?.assets?.assets[0]?.node.relatedContentsGrouped ?? undefined;
        if (searchContext?.value.product) return data?.product?.relatedContentsGrouped ?? undefined;
        if (searchContext?.value.article) return data?.article?.relatedContentsGrouped ?? undefined;

        return data?.contentsGrouped ?? undefined;
    };

    const groupedContents = computed((): DocumentationGroupResult[] | undefined => {
        const groups = selectGroupedResult(result.value)?.groups;
        if (!isGroupingEnabled.value || !groups) return undefined;

        return mapContentGroupsToGroupResult(groups, groupHits.value, getDataDisplayPropertiesByConfig);
    });

    const total = computed((): Total | undefined => {
        const totalGroups = selectGroupedResult(result.value)?.totalGroups;
        if (!isGroupingEnabled.value || !totalGroups) return undefined;

        return { count: totalGroups.count, countRelation: totalGroups.countRelation ?? undefined };
    });

    /**
     * Loads the next page of groups. Only the group pagination arguments are overridden - passing any
     * other variable would change the cache keys of the returned groups and make Apollo refetch the first
     * page, which results in an endless load-more loop.
     *
     * @param after - Cursor of the last already loaded group.
     * @param first - Number of groups to load.
     * @returns The cursor of the last loaded group, or `undefined` if no further group was returned.
     */
    const loadMoreGroups = async (after: string, first: number): Promise<string | undefined> => {
        const fetchResult = await fetchMore({ variables: { groupAfter: after, groupFirst: first } });

        const groups = selectGroupedResult(fetchResult?.data)?.groups;
        if (!groups || groups.length === 0) return undefined;

        return groups[groups.length - 1].cursor;
    };

    /*
    The already loaded items become invalid in two cases, so both drop them:
    - a changed filter, context or grouping criteria replaces the group list - the items belong to groups that are gone
    - a changed sorting keeps the group list (it is not sorted) but reorders the hits inside every group

    They are not re-requested here: the page resets the infinite scroll of every card (`itemsResetKey`), which
    scrolls the group back to the top and loads its first page again - consistent with the plain document list,
    which also falls back to its first page.
    */
    watch([sharedQueryVariables, () => toContentSort(sort?.value)], invalidateLoadedItems);

    /**
     * Requests one page of hits of a single group - the first page is requested when the user expands it.
     * Never call this directly: `loadMoreGroupItems` is the entry point that collapses concurrent calls.
     *
     * - Why not on the running watch query: its group list query selects no hits at all, and paging them
     *   there is impossible anyway, because the grouped result paginates the hits of every group with the
     *   same `hitsAfter` argument.
     * - What is done instead: the group is re-requested on its own with a slim selection set,
     *   `groups(first: 1, after: <cursor of the preceding group>)`.
     * - Why `network-only`: the loaded pages live in `groupHits` for the lifetime of the composable. A cache
     *   hit would only serve stale hits (e.g. deleted documents) when the document list is opened again.
     */
    const fetchGroupItems = async (groupCursor: string, first: number): Promise<string | undefined> => {
        const groups = selectGroupedResult(result.value)?.groups;
        const groupIndex = groups?.findIndex((group) => group.cursor === groupCursor) ?? -1;
        if (!groups || groupIndex < 0) return undefined;

        const loadedHits = groupHits.value[groupCursor] ?? [];
        //no hits loaded yet (the group was just expanded) - request its first page
        const hitsAfter = loadedHits.at(-1)?.cursor ?? "";

        const requestedGeneration = searchGeneration;

        const fetchResult = await consumerClient.query<GroupedQueryData<GroupItemsEntry>, GroupItemsQueryVariables>({
            query: groupItemsQuery.value,
            variables: {
                ...sharedQueryVariables.value,
                sort: toContentSort(sort?.value),
                acceptedLanguages: languageStore.dataQueryLanguages,
                groupAfter: groupIndex > 0 ? groups[groupIndex - 1].cursor : "",
                hitsFirst: first,
                hitsAfter,
            },
            fetchPolicy: "network-only",
        });

        //the search changed while the page was loading - its hits belong to a result that is gone
        if (requestedGeneration !== searchGeneration) return undefined;

        const knownCursors = new Set(loadedHits.map((hit) => hit.cursor));
        const hits = (selectGroupedResult(fetchResult.data)?.groups?.[0]?.hits ?? []).filter(
            (hit) => !knownCursors.has(hit.cursor)
        );
        if (hits.length === 0) return undefined;

        groupHits.value = {
            ...groupHits.value,
            [groupCursor]: [...loadedHits, ...hits],
        };

        return hits.at(-1)?.cursor;
    };

    /**
     * Loads the next page of items (hits) of a single group.
     *
     * Concurrent calls for the same group share one request: the infinite scroll can fire again before the
     * previous page resolved, and both calls would compute the same `hitsAfter` and duplicate its hits.
     *
     * @param groupCursor - Cursor of the group whose items should be extended.
     * @param first - Number of items to load.
     * @returns The cursor of the last loaded item, or `undefined` if no further item was returned.
     */
    const loadMoreGroupItems = (groupCursor: string, first: number): Promise<string | undefined> => {
        const runningRequest = groupItemRequests.get(groupCursor);
        if (runningRequest) return runningRequest;

        const request: Promise<string | undefined> = fetchGroupItems(groupCursor, first).finally(() => {
            //a new search may already have registered a newer request for this group - never drop that one
            if (groupItemRequests.get(groupCursor) === request) groupItemRequests.delete(groupCursor);
        });
        groupItemRequests.set(groupCursor, request);

        return request;
    };

    return {
        groupedContents,
        total,
        loading,
        error,
        loadMoreGroups,
        loadMoreGroupItems,
    };
};
